import React, { useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Select, MenuItem, TextField, InputAdornment, CircularProgress, Box } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { Search } from "@mui/icons-material";
import OutlinedInput from "@mui/material/OutlinedInput";
import { FORM_PROPERTIES } from "../InputField";
import { useSelectWithSearch } from "./hooks/useSelectWithSearch";
import {
	selectWithSearchSx,
	menuPaperSxWithScroll,
	menuListPropsSx,
	menuZIndexSx,
	searchMenuItemSx,
	searchTextFieldSx,
	loadingMenuItemSx,
	noResultsMenuItemSx,
	loadingIndicatorSx,
	selectRenderValuePlaceholderSx,
	selectRenderValueSelectedSx,
	menuItemSx,
} from "./styles/SelectWithSearch.styles";
import { SelectFieldWrapper } from "../Select/shared/SelectFieldWrapper";
import {
	formSelectPlaceholderClassName,
	formSelectValueClassName,
	mergeSelectSx,
	outlinedInputExternalLabelProps,
	selectOutlinedInputSx,
} from "@config/theme/formControls";

interface SelectWithSearchProps {
	label?: string;
	placeholder?: string;
	value: string;
	onChange: (value: string) => void;
	options: Array<{ value: string; label: string }>;
	loading?: boolean;
	disabled?: boolean;
	required?: boolean;
	error?: boolean;
	helperText?: string;
	startIcon?: React.ReactNode;
	searchPlaceholder?: string;
	onSearchChange?: (searchTerm: string) => void;
	onScrollToBottom?: () => void;
	hasNextPage?: boolean;
	isFetchingNextPage?: boolean;
	searchDebounceMs?: number;
	isSearchEnabled?: boolean;
	id?: string;
	className?: string;
	customSx?: SxProps<Theme>;
	/** Chevron cap + padding reserve; use `useAuthFormControlHeight()` on compact auth forms. */
	controlHeight?: number;
	labelClassName?: string;
}

const SelectWithSearch: React.FC<SelectWithSearchProps> = ({
	label,
	placeholder,
	value,
	onChange,
	options = [],
	loading = false,
	disabled = false,
	required = false,
	error = false,
	helperText = "",
	startIcon,
	searchPlaceholder = "Search...",
	onSearchChange,
	onScrollToBottom,
	hasNextPage = false,
	isFetchingNextPage = false,
	searchDebounceMs = 500,
	isSearchEnabled = false,
	id,
	className = "",
	customSx,
	controlHeight,
	labelClassName,
}) => {
	const { t } = useTranslation();
	const resolvedPlaceholder = placeholder ?? t("common.singleSelect.selectPlaceholder");
	const { searchTerm, setSearchTerm, isOpen, menuRef, handleClose, handleOpen } = useSelectWithSearch({
		onSearchChange,
		searchDebounceMs,
		isSearchEnabled,
	});

	// Store the selected option label separately to persist even if options change
	const [selectedLabel, setSelectedLabel] = React.useState<string>("");

	// Ref for the search input to focus it when dropdown opens
	const searchInputRef = React.useRef<HTMLInputElement>(null);

	const selectedOption = useMemo(() => {
		if (!value || value === "") return undefined;
		return options.find(opt => opt.value === value || opt.value === String(value));
	}, [options, value]);

	// Compute the actual value to pass to Select - if value doesn't exist in options, use empty string
	// This prevents MUI warnings about out-of-range values
	const safeValue = useMemo(() => {
		if (!value || value === "" || options.length === 0) {
			return "";
		}
		// Check if value exists in options
		const exists = options.some(opt => opt.value === value || opt.value === String(value));
		return exists ? value : "";
	}, [value, options]);

	// Focus search input when dropdown opens and search is enabled
	useEffect(() => {
		if (isOpen && isSearchEnabled && searchInputRef.current) {
			// Small delay to ensure the menu is fully rendered
			setTimeout(() => {
				searchInputRef.current?.focus();
			}, 100);
		}
	}, [isOpen, isSearchEnabled]);

	// Update selectedLabel when we find the option in the list
	useEffect(() => {
		if (selectedOption?.label) {
			setSelectedLabel(selectedOption.label);
		} else if (!value || value === "") {
			// Clear label when value is cleared
			setSelectedLabel("");
		}
	}, [selectedOption?.label, value]);

	// Initialize label on mount if value exists but no label found yet
	useEffect(() => {
		if (value && options.length > 0) {
			const option = options.find(opt => opt.value === value);
			if (option?.label && !selectedLabel) {
				setSelectedLabel(option.label);
			}
		} else if (!value || value === "" || options.length === 0) {
			// Clear label when value is empty or options are empty
			setSelectedLabel("");
		}
	}, [value, options, selectedLabel]);

	// If value changes but we don't have it in options yet, keep the previous label
	// But clear label if options are empty or value is empty
	const displayLabel = (!value || value === "" || options.length === 0) ? "" : (selectedOption?.label || selectedLabel);

	// Handle scroll to load more
	const handleMenuScroll = (event: React.UIEvent<HTMLElement>) => {
		const target = event.currentTarget;
		const scrollTop = target.scrollTop;
		const scrollHeight = target.scrollHeight;
		const clientHeight = target.clientHeight;

		// Load more when near bottom (within 50px)
		if (scrollHeight - scrollTop - clientHeight < 50 && hasNextPage && !isFetchingNextPage && onScrollToBottom) {
			onScrollToBottom();
		}
	};

	const handleChange = (event: { target: { value: unknown } }) => {
		const newValue = event.target.value as string;
		if (newValue !== "") {
			// Find and store the label before changing value
			const option = options.find(opt => opt.value === newValue);
			if (option?.label) {
				setSelectedLabel(option.label);
			}
			onChange(newValue);
		} else {
			// Clear label when value is cleared
			setSelectedLabel("");
			onChange("");
		}
	};

	return (
		<div>
			{label && (
				<label
					className={`mb-2 block font-medium text-sm sm:text-base ${labelClassName ?? ""}`}
					htmlFor={id}>
					{label}
					{required && <span className="text-red-500">{FORM_PROPERTIES.REQUIRED_ASTERIK}</span>}
				</label>
			)}
			<SelectFieldWrapper controlHeight={controlHeight} disabled={disabled || loading}>
			<Select
				value={safeValue}
				onChange={handleChange}
				disabled={disabled || loading}
				fullWidth
				displayEmpty
				error={error}
				className={className}
				sx={
					customSx
						? mergeSelectSx(
								error,
								undefined,
								{
									"& .MuiOutlinedInput-root": {
										"& .MuiInputAdornment-root": { marginRight: "-3px" },
									},
								},
								customSx,
							)
						: selectWithSearchSx(error)
				}
				open={isOpen}
				onOpen={handleOpen}
				onClose={handleClose}
				MenuProps={{
					PaperProps: {
						sx: menuPaperSxWithScroll,
						onScroll: handleMenuScroll as React.UIEventHandler<HTMLDivElement>,
						ref: menuRef,
					},
					MenuListProps: {
						component: "div",
						sx: menuListPropsSx,
						onScroll: handleMenuScroll as React.UIEventHandler<HTMLUListElement>,
						// Disable auto-highlighting when search is enabled
						autoFocus: false,
						// Prevent keyboard navigation from highlighting items when search is enabled
						...(isSearchEnabled && {
							onKeyDown: (e: React.KeyboardEvent) => {
								// If a character key is pressed (not arrow keys, enter, escape, tab, etc.), redirect to search input
								// This prevents Material-UI Select from highlighting items based on typed characters
								if (e.key.length === 1 && e.key !== " " && !e.ctrlKey && !e.metaKey && !e.altKey &&
								    e.key !== "ArrowUp" && e.key !== "ArrowDown" && e.key !== "Enter" && e.key !== "Escape" && e.key !== "Tab") {
									// Prevent Material-UI from handling the key
									e.preventDefault();
									e.stopPropagation();

									if (searchInputRef.current) {
										// Focus the search input
										searchInputRef.current.focus();

										// Get current value from React state and append the new character
										const currentValue = searchTerm || "";
										const newValue = currentValue + e.key;

										// Set the value using React's setter (this will trigger the debounced search)
										setSearchTerm(newValue);

										// Also update the native input value to ensure it's visible immediately
										const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;
										if (nativeInputValueSetter) {
											nativeInputValueSetter.call(searchInputRef.current, newValue);
										}

										// Set cursor position to end
										if (searchInputRef.current.setSelectionRange) {
											searchInputRef.current.setSelectionRange(newValue.length, newValue.length);
										}

										// Trigger input event to ensure React state updates
										const inputEvent = new Event('input', { bubbles: true });
										searchInputRef.current.dispatchEvent(inputEvent);
									}
								}
							},
						}),
					},
					// Disable auto-highlighting to prevent keyboard navigation from highlighting items
					autoFocus: false,
					// Ensure dropdown appears above modals (modal z-index is 9999)
					sx: menuZIndexSx,
				}}
				input={
					<OutlinedInput
						{...outlinedInputExternalLabelProps}
						sx={selectOutlinedInputSx(error)}
						startAdornment={
							startIcon ? (
								<InputAdornment sx={{ marginRight: "-3px" }} position="start">
									{startIcon}
								</InputAdornment>
							) : undefined
						}
						{...(isSearchEnabled && isOpen && {
							onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
								// If a character key is pressed when dropdown is open, redirect to search input
								if (e.key.length === 1 && e.key !== " " && !e.ctrlKey && !e.metaKey && !e.altKey) {
									if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
										// Prevent Material-UI from handling the key
										e.preventDefault();
										e.stopPropagation();

										// Focus the search input
										searchInputRef.current.focus();

										// Get current value and append the new character
										const currentValue = searchInputRef.current.value || "";
										const newValue = currentValue + e.key;

										// Set the value using React's setter
										setSearchTerm(newValue);

										// Also update the native input value to ensure it's in sync
										const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;
										if (nativeInputValueSetter) {
											nativeInputValueSetter.call(searchInputRef.current, newValue);
										}

										// Trigger input event to ensure React state updates
										const inputEvent = new Event('input', { bubbles: true });
										searchInputRef.current.dispatchEvent(inputEvent);
									}
								}
							},
						})}
					/>
				}
				renderValue={() => {
					const hasSelection = Boolean(value && value !== "");
					const resolvedLabel =
						options.find(opt => opt.value === value || opt.value === String(value))?.label ||
						selectedLabel ||
						displayLabel ||
						"";
					const valueStyle = {
						...(hasSelection && resolvedLabel
							? (selectRenderValueSelectedSx as React.CSSProperties)
							: (selectRenderValuePlaceholderSx as React.CSSProperties)),
						...(disabled
							? {
									color: "#6B7280",
									WebkitTextFillColor: "#6B7280",
								}
							: {}),
					};

					if (hasSelection && resolvedLabel) {
						return (
							<span className={formSelectValueClassName} style={valueStyle}>
								{resolvedLabel}
							</span>
						);
					}

					return (
						<span className={formSelectPlaceholderClassName} style={valueStyle}>
							{resolvedPlaceholder}
						</span>
					);
				}}>
				{/* Search Input - Only show if search is enabled */}
				{isSearchEnabled && (
					<MenuItem
						disableRipple
						onClick={e => e.stopPropagation()}
						onMouseDown={e => e.stopPropagation()}
						sx={searchMenuItemSx}>
						<TextField
							inputRef={searchInputRef}
							size="small"
							placeholder={searchPlaceholder}
							variant="outlined"
							value={searchTerm}
							onChange={e => setSearchTerm(e.target.value)}
							fullWidth
							autoFocus={false}
							onClick={e => e.stopPropagation()}
							onMouseDown={e => e.stopPropagation()}
							onKeyDown={e => {
								// Prevent arrow keys from navigating Select items when typing in search
								if (e.key === "ArrowUp" || e.key === "ArrowDown") {
									e.stopPropagation();
								}
							}}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<Search sx={{ fontSize: "18px", color: "#98A2B3" }} />
									</InputAdornment>
								),
							}}
							sx={searchTextFieldSx}
						/>
					</MenuItem>
				)}

				{/* Options */}
				{loading && options.length === 0 ? (
					<MenuItem disabled>
						<Box sx={loadingMenuItemSx}>Loading...</Box>
					</MenuItem>
				) : options.length > 0 ? (
					options.map(option => (
						<MenuItem key={option.value} value={option.value} sx={menuItemSx(false)}>
							{option.label}
						</MenuItem>
					))
				) : (
					<MenuItem disabled>
						<Box sx={noResultsMenuItemSx}>
							{isSearchEnabled && searchTerm ? "No results found" : "No options available"}
						</Box>
					</MenuItem>
				)}

				{/* Loading indicator for next page */}
				{isFetchingNextPage && (
					<MenuItem disabled>
						<Box sx={loadingIndicatorSx}>
							<CircularProgress size={20} />
						</Box>
					</MenuItem>
				)}
			</Select>
			</SelectFieldWrapper>
			{helperText && (
				<p className={`mt-1 ${error ? "text-[#d32f2f]" : "text-gray-500"}`} style={{ fontSize: "11px" }}>
					{helperText}
				</p>
			)}
		</div>
	);
};

export default SelectWithSearch;
